import { ComponentProps, createContext, ReactNode, useContext, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ProductFruits, useProductFruitsApi } from 'react-product-fruits';
import { useUser } from 'routes/dataroutes/UserData';
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";


interface ProductFruitsContextType {
  userInfo: any;
}

const ProductFruitsContext = createContext<ProductFruitsContextType | null>(null);

interface ProductFruitsProviderProps {
  children: ReactNode;
}

export default function ProductFruitsProvider({ children }: ProductFruitsProviderProps) {
  const user = useUser();
  const { activeSubscription } = useActiveSubscription();

  const userInfo = useMemo(() => {
    if (!user) return undefined;

    const userProps: Record<
      string,
      string | number | boolean | string[] | number[] | any
    > = {};

    const userFields = [
      "planName",
      "planSlug",
      "isFreeTrial",
      "hasPaymentIssues",
      "madeFirstRequest",
      "onboardedForProduct",
      "apiCallLimit",
      "concurrentRequestLimit",
      "geotargetingEnabled",
    ];

    userFields.forEach((field) => {
      const userProperty = user[field as keyof typeof user];
      if (userProperty !== undefined) {
        userProps[field] = userProperty;
      }
    });

    if (user.signedUpAt) {
      userProps.accountAge = Math.floor(
        (Date.now() - new Date(user.signedUpAt).getTime()) /
          (1000 * 60 * 60 * 24)
      );
    }

    const userData = {
      username: user.id.toString(),
      email: user.email,
      signUpAt: user.signedUpAt || new Date().toISOString(),
      role: activeSubscription?.planSlug || "free_user",
      props: Object.keys(userProps).length > 0 ? userProps : undefined,
    };

    return userData;
  }, [ user, activeSubscription?.planSlug ]);

  const contextValue = useMemo<ProductFruitsContextType>(() => ({
    userInfo,
  }), [userInfo]);

  return (
    <ProductFruitsContext.Provider value={contextValue}>
      {/* children stay outside the boundary, so a Product Fruits failure can't take down the page */}
      <ErrorBoundary fallback={null}>
        <ProductFruitsSetup userInfo={userInfo} />
      </ErrorBoundary>
      {children}
    </ProductFruitsContext.Provider>
  );
}

type ProductFruitsUser = ComponentProps<typeof ProductFruits>["user"];

// Loads Product Fruits and tracks new signups
function ProductFruitsSetup({ userInfo }: { userInfo?: ProductFruitsUser }) {
  const user = useUser();

  // Track custom event for new signups (within 3 months)
  useProductFruitsApi((api) => {
    if (api && userInfo && user?.signedUpAt) {
      const signUpDate = new Date(user.signedUpAt);
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

      if (signUpDate >= threeMonthsAgo) {
        // Check if we've already tracked this event for this user
        // Use the same hash function as feature flags for consistency
        const hash = (user.id * 9301 + 49297) % 233280;
        const storageKey = `pf_new_user_signup_${hash}`;
        const alreadyTracked = localStorage.getItem(storageKey);

        if (!alreadyTracked) {
          // Product Fruits only creates api.events when its server enables it.
          // It was missing for every user during their outage on 19-21 Sept 2026 (ENG-1236)
          if (!api.events?.track) {
            console.warn("PF api.events is missing.");
            return;
          }

          api.events.track('new_user_signup', userInfo, {
            forwardToIntegrations: true
          });

          // Mark as tracked
          localStorage.setItem(storageKey, 'true');
        }
      }
    }
  }, [userInfo, user?.signedUpAt]);

  if (!userInfo) return null;

  return (
    <ProductFruits
      workspaceCode={process.env.REACT_APP_PRODUCT_FRUITS_WORKSPACE_CODE!}
      language="en"
      user={userInfo}
    />
  );
}

export function useProductFruits() {
  const context = useContext(ProductFruitsContext);
  if (!context) {
    throw new Error('useProductFruits must be used within a ProductFruitsProvider');
  }
  return context;
}
